import { Badge } from '../../primitives/Badge.tsx';
import { Icon } from '../../primitives/icon/index.ts';
import { Spinner } from '../../primitives/Spinner.tsx';
import { Text } from '../../primitives/Text.tsx';
import { Waveform } from './Waveform.tsx';
import styles from './AudioControl.module.css';

/**
 * Design system § 14 — the audio / listening control. The product signature.
 *
 * Wherever audio is present it is the largest object on the screen and the only
 * one glowing (§ 1.5). It is NEVER replaced by a generic play button, and the
 * rotating conic gradient it carries appears in no other component (§ 4). The
 * frame is a band, not an outline: § 14 gives it `--orb-band` of the diameter,
 * because the gradient IS the mass of the object.
 *
 * This component owns no audio element and no playback. It takes a state and
 * reports intent; the hook that actually plays a sample lives in the feature
 * layer, because what is being played is domain knowledge and this library has
 * none (research D-025).
 *
 * Replay is unlimited and UNCOUNTED — there is deliberately no way to pass a
 * replay count, because § 14 forbids showing one.
 */

export type AudioControlState = 'idle' | 'playing' | 'paused' | 'replay' | 'loading' | 'error';

interface AudioControlProps {
  state?: AudioControlState;
  /**
   * The accessible name of the action, in the caller's own words — this
   * library does not know whether the sound is a note, a chord or a phrase.
   */
  label: string;
  /** The visible state chip beside the orb. Text, so § 12's colour rule holds. */
  statusLabel?: string;
  /** Shown in the `error` state, with the retry the state requires. */
  errorMessage?: string;
  /**
   * `default` is the § 14 product signature and the only size a learner ever
   * sees. `compact` exists for admin authoring surfaces, where the control is
   * a review affordance beside a form field rather than the dominant object
   * on the screen (§ 16 density is an open decision for admin surfaces).
   */
  size?: 'default' | 'compact';
  onActivate?: () => void;
}

export function AudioControl({
  state = 'idle',
  label,
  statusLabel,
  errorMessage,
  size = 'default',
  onActivate,
}: AudioControlProps) {
  const animated = state === 'playing';
  const disabled = state === 'loading';

  return (
    /*
      The orb, the chip and the message are one centred column. Nothing in it
      is taken out of flow, so the chip can never come to rest over the
      waveform — or, as it did while this wrapper was unpositioned, over
      whatever happens to sit at the bottom of the page.
    */
    <div className={styles.root} data-size={size}>
      <button
        type="button"
        className={styles.control}
        data-state={state}
        disabled={disabled}
        onClick={onActivate}
        aria-label={label}
      >
        {/*
          The frame is the silhouette and the clip; the paint is grad/audio,
          and it is the paint that rotates (§ 4.1). Two elements rather than
          one because an asymmetric shape that turns wobbles — what the
          reference rotates is the light, not the object.
        */}
        <span className={styles.frame} aria-hidden="true">
          <span className={styles.paint} data-motif="audio-paint" />
        </span>
        <span className={styles.rings} aria-hidden="true" />

        <span className={styles.core} data-motif="audio-core">
          {state === 'loading' ? (
            <Spinner label={label} size={32} />
          ) : state === 'replay' ? (
            /* § 14 — the replay glyph replaces the waveform. */
            <Icon name="rotate-ccw" size={32} />
          ) : (
            <Waveform animated={animated} />
          )}
        </span>
      </button>

      {/*
        The state chip § 14 asks for alongside a playing orb, set directly
        below it. It is what carries the state under prefers-reduced-motion,
        where the rotation and the waveform are frozen (§ 9).
      */}
      {statusLabel ? (
        <div className={styles.status}>
          <Badge tone={state === 'error' ? 'near-miss' : 'accent'}>{statusLabel}</Badge>
        </div>
      ) : null}

      {/*
        § 14 error: an explicit textual state, with retry, and the dependent
        controls disabled — which is the caller's business, since only the
        caller knows what depends on the sound.
      */}
      {state === 'error' && errorMessage ? (
        <p className={styles.message}>
          <Text variant="body-sm" tone="error">
            {errorMessage}
          </Text>
        </p>
      ) : null}
    </div>
  );
}
